import { keyBy, reduce } from 'lodash';

export const timeplanStatuses = [
  { id: 'vacancy', name: 'Vacancy', disabled: false },
  { id: 'to_propose', name: 'To propose', disabled: false },
  { id: 'pending_approval', name: 'Pending approval', disabled: false },
  { id: 'approved', name: 'Approved', disabled: false },
  { id: 'on_board', name: 'On board', disabled: true },
  { id: 'completed', name: 'Completed', disabled: true },
  { id: 'other_agency', name: 'Other agency', disabled: false },
];

export const timeplanStatusesById = keyBy(timeplanStatuses, 'id');

export const optionsOfTimeplanStatuses = timeplanStatuses.map(({ id, name, disabled }) => ({
  value: id,
  label: name,
  disabled,
}));

export const timeplanStatusByValue = keyBy(optionsOfTimeplanStatuses, 'value');

/**
 * isPeriodValid
 * @param {Array} periods
 * @param periodToValidate
 * @returns {Boolean}
 */
export const isPeriodValid = (periods, periodToValidate) => {
  const { start: s1, end: e1, id: id1 } = periodToValidate;

  return periods
    .reduce((isValid, { id, start: s2, end: e2 }) => {
      if (id1 === id) return isValid;
      if (!isValid) return isValid;

      const case1 = s2 < s1 && s1 < e2;
      const case2 = s2 < s1 && s1 < e2;
      const case3 = (s1 < s2 && s2 < e1) && (s1 < e2 && e2 < e1);

      return !(case1 || case2 || case3);
    }, true);
};


export const normalizeFilterObj = (filter) => reduce(filter, (acc, isSelect, field) => {
  if (field === "additionalFilter") {
    return {...acc, [field]: isSelect}
  } else {
    const [type, id] = field.split('.');
    const fieldName = {
      vessels: 'vesselIds',
      roles: 'roleIds',
      owners: 'ownerIds',
    }[type];

    return isSelect ? { ...acc, [fieldName]: [...acc[fieldName], id] } : acc;
  }
}, { roleIds: [], vesselIds: [], ownerIds: [] });

/**
 * getNoUserNameTextByStatus
 * @param {String} status
 * @param {String} username
 * @returns {string}
 */
export const getNoUserNameTextByStatus = (status, username, promotionsCount) => (
  status === 'other_agency'
    ? 'Other agency'
    : getUsernameWithPromotions(username, promotionsCount, status) || 'Vacancy'
);
const getUsernameWithPromotions = (username, promotionsCount, status) => {
  const hasNotCompletedStatus = !"completed".includes(status) 
  const hasPromotions = promotionsCount > 0;

  if (hasPromotions && hasNotCompletedStatus ) {
    return `${username}⬆`;
  }
  return username;
};
export const noUserAva = 'https://cdn4.iconfinder.com/data/icons/users-and-user-settings-line/120/select_user-512.png';

// CREW-1252 — slot statuses that never carry a CREWISOR seafarer: another
// agency crews the position, or nobody has been found yet. The Off/On signers
// counter counts seafarers, so these positions are not in it. Same pair the
// backend's `StatusManager#crew_change` leaves out of status transitions.
const NO_SEAFARER = {
  other_agency: { one: 'Other agency', many: 'Other agency', kind: 'Other agency' },
  vacancy: { one: 'vacancy', many: 'vacancies', kind: 'Vacancy' },
};

const emptySide = () => ({ other_agency: 0, vacancy: 0 });

/**
 * uncountedPositions
 * @param {Array} rows - crew-list rows or pairs carrying `sign_off_status` / `sign_on_status`
 * @returns {{positions: number, total: number, off: Object, on: Object}}
 *   `positions` — filled sides; `total` — how many of them hold no seafarer by design
 */
export const uncountedPositions = (rows) => (rows || []).reduce((acc, row) => {
  [['off', row.sign_off_status], ['on', row.sign_on_status]].forEach(([side, status]) => {
    if (!status) return;

    acc.positions += 1;
    if (NO_SEAFARER[status]) {
      acc[side][status] += 1;
      acc.total += 1;
    }
  });

  return acc;
}, { positions: 0, total: 0, off: emptySide(), on: emptySide() });

const describeSide = (counts) => Object.keys(NO_SEAFARER)
  .filter((status) => counts[status])
  .map((status) => {
    const n = counts[status];
    return `${n} ${n === 1 ? NO_SEAFARER[status].one : NO_SEAFARER[status].many}`;
  })
  .join(', ');

// "sign off — 1 Other agency; sign on — 2 vacancies"
export const describeUncounted = ({ off, on }) => [['sign off', off], ['sign on', on]]
  .map(([label, counts]) => [label, describeSide(counts)])
  .filter(([, text]) => text)
  .map(([label, text]) => `${label} — ${text}`)
  .join('; ');

// "Other agency · Vacancy" — what the positions are, without the numbers.
export const uncountedKinds = ({ off, on }) => Object.keys(NO_SEAFARER)
  .filter((status) => off[status] || on[status])
  .map((status) => NO_SEAFARER[status].kind)
  .join(' · ');

// Every position is one of them — a 0/0 that is expected, not lost crew.
export const onlyUncounted = ({ positions, total }) => positions > 0 && total === positions;
